'use client';

import { cn } from '@/utils/tailwind-merge';
import React, { SelectHTMLAttributes, forwardRef, useState } from 'react';
import { FieldError, UseFormRegister } from 'react-hook-form';

interface CustomSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
    required?: boolean;
    className?: string;
    register?: UseFormRegister<any>;
    error?: FieldError | string;
    options: Array<{ label: string; value: string | number }>;
    isBlack?: boolean;
    placeholder?: string;
}

const Select = forwardRef<HTMLSelectElement, CustomSelectProps>((props, ref) => {
    const { className, required, register, error, options, isBlack, placeholder, ...selectProps } =
        props;
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState<string | number | undefined>('');

    const toggleDropdown = () => setIsOpen((prev) => !prev);

    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedValue(event.target.value);
        selectProps.onChange && selectProps.onChange(event);
    };

    return (
        <div className={cn('relative flex flex-col', className)}>
            <select
                {...register}
                {...selectProps}
                ref={ref}
                onClick={toggleDropdown}
                onBlur={() => setIsOpen(false)}
                onChange={handleChange}
                className={cn(
                    'h-[72px] w-full border px-6 truncate lg:text-h5 max-lg:text-h6 bg-black focus:border-border-white focus:outline-none border-white rounded-none uppercase appearance-none leading-none placeholder-white/50',
                    error && 'border-red',
                    selectedValue || isOpen ? 'text-white' : 'text-white/50',
                )}
                required={required}
                value={selectedValue}
                defaultValue={placeholder ? '' : undefined}
            >
                {placeholder && (
                    <option value="" disabled hidden>
                        {required ? `${placeholder}*` : placeholder}
                    </option>
                )}
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
            {error && <span className="text-red text-sm">{error as string}</span>}

            <span
                className={cn(
                    'absolute right-4 top-9 transform -translate-y-1/2 transition-transform duration-300',
                    isBlack ? 'text-black' : 'text-tofu',
                    !isOpen && 'rotate-180',
                )}
            >
                <ChevronIcon color="white" />
            </span>
        </div>
    );
});

Select.displayName = 'Select';

export default Select;

export function ChevronIcon({ color }: { color: 'black' | 'white' }) {
    return (
        <svg width="12" height="8" viewBox="0 0 9 4" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M4.5 -3.93402e-07L9 4L0 4L4.5 -3.93402e-07Z"
                fill={color === 'black' ? 'black' : '#E8E4DA'}
            />
        </svg>
    );
}
